import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useParent } from '../../context/ParentContext';
import { useAuth } from '../../context/AuthContext';
import {
  availableTeams, contextKey, contextDisplayLabel, hasOwnTeamWorkspace,
} from '../../lib/accountContexts';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import { ChevronDown, Check, User, Users, Briefcase } from 'lucide-react';
import { toast } from 'sonner';

/**
 * Explicit person/view switcher.
 * Players with an explicit guardian link can switch to their child and back.
 * Leaders retain their existing team workspace.
 * Selecting a child is an explicit, reversible view change and never changes
 * the user's team role.
 */
export default function ChildSwitcher() {
  const {
    contexts, active, setActiveContext, clearActiveContext,
    loading, error, refresh, switching, isParentView, leaderWorkspace,
  } = useParent();
  const { user, teams, activeTeam, teamSwitching, changeActiveTeam } = useAuth();
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  const disabled = busy || switching || teamSwitching || loading;
  const teamIds = new Set(availableTeams(teams).map(team => team.id));
  const hasOwnWorkspace = hasOwnTeamWorkspace(user, activeTeam)
    || availableTeams(teams).some(team => hasOwnTeamWorkspace(user, team));
  const ownTeam = hasOwnTeamWorkspace(user, activeTeam) ? activeTeam
    : availableTeams(teams).find(team => hasOwnTeamWorkspace(user, team));

  const handlePick = async (link) => {
    if (disabled) return;
    setBusy(true);
    try {
      await setActiveContext({ playerProfileId: link.player_profile_id, teamId: link.team_id });
      navigate('/oversikt', { replace: true });
    } catch (err) {
      toast.error(err?.message || 'Kunde inte byta till barnvyn.');
    } finally { setBusy(false); }
  };

  const handleOwnWorkspace = async () => {
    if (disabled || !ownTeam) return;
    setBusy(true);
    try {
      await changeActiveTeam(ownTeam.id);
      clearActiveContext();
      navigate('/oversikt', { replace: true });
    } catch (err) { toast.error(err?.message || 'Kunde inte byta till spelarläget.'); }
    finally { setBusy(false); }
  };

  // A leader's main workspace must not be replaced by a parent/child picker.
  // The guardian link remains available to backend features and future
  // explicit parent mode, but it is not a navigation context for leaders.
  if (leaderWorkspace) return null;
  if (loading && !contexts.length && !isParentView) return hasOwnWorkspace ? null : <p className="px-2 text-xs text-muted-foreground">Laddar barn…</p>;
  if (error && !isParentView) return <button type="button" onClick={refresh} className="px-2 text-xs text-muted-foreground">Barnens lag kunde inte hämtas. Försök igen.</button>;
  if (!contexts.length && !isParentView) return null;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button type="button" disabled={disabled}
          className="flex w-full items-center gap-2 rounded-lg border border-border bg-muted/30 px-3 py-2 text-left text-xs disabled:opacity-50"
          data-testid="child-switcher-trigger" aria-label="Välj barn och lag">
          {active ? <User className="h-4 w-4 shrink-0 text-primary" /> : <Briefcase className="h-4 w-4 shrink-0" />}
          <span className="min-w-0 flex-1">
            <span className="block truncate font-medium">{isParentView && !active ? 'Välj barn' : contextDisplayLabel(active, activeTeam, user)}</span>
            <span className="block truncate text-[11px] text-muted-foreground">
              {active ? (active.team_name || 'Barnvy') : (isParentView ? 'Välj barn och lag' : 'Växla mellan dig och ditt barn')}
            </span>
          </span>
          <ChevronDown className="h-3.5 w-3.5 shrink-0" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-72 max-h-80 overflow-y-auto bg-card border-border" data-testid="child-switcher-menu">
        <DropdownMenuLabel>Välj barn och lag</DropdownMenuLabel>
        {hasOwnWorkspace && (
          <DropdownMenuItem
            disabled={disabled || !isParentView}
            onClick={handleOwnWorkspace}
            data-testid="child-switcher-own-workspace"
          >
            <Briefcase className="mr-2 h-4 w-4" />
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm">Mitt spelarläge</p>
              <p className="truncate text-xs text-muted-foreground">{ownTeam?.name || 'Ditt lag'}</p>
            </div>
            {!isParentView && <Check className="ml-2 h-4 w-4 text-primary" />}
          </DropdownMenuItem>
        )}
        <DropdownMenuSeparator />
        <DropdownMenuLabel className="flex items-center gap-2 text-xs text-muted-foreground">
          <Users className="h-3 w-3" /> Mina barn
        </DropdownMenuLabel>
        {error && <DropdownMenuItem onClick={refresh}>{error}</DropdownMenuItem>}
        {!loading && !error && !contexts.length && <DropdownMenuLabel>Inga barn att välja.</DropdownMenuLabel>}
        {contexts.map(link => {
          const key = contextKey(link.player_profile_id, link.team_id);
          const selected = active && key === contextKey(active.player_profile_id, active.team_id);
          const available = teamIds.has(link.team_id);
          return (
            <DropdownMenuItem key={key} disabled={disabled || !available} onClick={() => handlePick(link)} data-testid={`child-switcher-item-${key}`}>
              <User className="mr-2 h-4 w-4 text-primary" />
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm">{link.player_name || 'Barn'}</p>
                <p className="truncate text-xs text-muted-foreground">{link.team_name || 'Lag'}{!available && ' · Inte tillgängligt'}</p>
              </div>
              {selected && <Check className="ml-2 h-4 w-4 text-primary" />}
            </DropdownMenuItem>
          );
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
